import type { ProblemDetails } from '@onflow/shared';

import { useAuthStore } from '../stores/authStore.js';

import { toast } from './toast.js';

/**
 * API base URL. The backend enables URI versioning (VersioningType.URI,
 * prefix 'v'), so every non-health route lives under `/v1`. We bake that into
 * the base URL so callers pass tidy paths like `/workouts`.
 */
const API_ROOT = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';
const API_URL = `${API_ROOT}/v1`;

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
    public readonly requestId?: string,
    public readonly fieldErrors?: Array<{ field: string; code: string; message?: string }>,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

interface RequestOptions extends RequestInit {
  auth?: boolean;
  json?: unknown;
  /** If absent, a UUID is generated so retries are idempotent once G15 ships. */
  idempotencyKey?: string;
  /** Override the base URL — used by refresh (hits non-versioned /auth endpoint via base). */
  absolutePath?: boolean;
}

let refreshInFlight: Promise<string | null> | null = null;

/**
 * Fetches a new access token using the httpOnly refresh cookie.
 * De-duplicates concurrent refreshes so a page with many parallel queries
 * only issues one refresh round-trip.
 */
async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        useAuthStore.getState().clear();
        return null;
      }
      const body = (await res.json()) as { accessToken: string; accessTokenExpiresAt: string };
      useAuthStore.getState().setSession(body.accessToken, body.accessTokenExpiresAt);
      return body.accessToken;
    } catch {
      useAuthStore.getState().clear();
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

function genRequestId(): string {
  // crypto.randomUUID is available in all evergreen browsers we target.
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  // Fallback for test environments that polyfill crypto partially.
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { auth = true, json, headers, idempotencyKey, absolutePath, ...init } = opts;

  const headerBag = new Headers(headers);
  const method = (init.method ?? 'GET').toUpperCase();
  const requestId = genRequestId();
  headerBag.set('X-Request-Id', requestId);

  if (json !== undefined) {
    headerBag.set('Content-Type', 'application/json');
    init.body = JSON.stringify(json);
  }
  if (auth) {
    const token = useAuthStore.getState().accessToken;
    if (token) headerBag.set('Authorization', `Bearer ${token}`);
  }
  // Mutations get an idempotency key so retries don't create duplicates.
  if (method === 'POST' || method === 'PATCH' || method === 'PUT') {
    headerBag.set('Idempotency-Key', idempotencyKey ?? genRequestId());
  }

  const base = absolutePath ? API_ROOT : API_URL;
  const run = () =>
    fetch(`${base}${path}`, {
      ...init,
      headers: headerBag,
      credentials: 'include',
    });

  let res = await run();

  if (res.status === 401 && auth) {
    const fresh = await refreshAccessToken();
    if (fresh) {
      headerBag.set('Authorization', `Bearer ${fresh}`);
      res = await run();
    }
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as ProblemDetails | null;
    const err = new ApiRequestError(
      res.status,
      body?.code ?? 'INTERNAL',
      body?.detail ?? body?.title ?? res.statusText,
      body,
      body?.requestId ?? requestId,
      body?.errors,
    );
    // Skip toast noise for expected 401s — the auth-refresh flow handles them
    // and they'd otherwise fire on every soft-expire.
    if (res.status !== 401) {
      toast.error(
        err.message,
        body?.requestId ? `Request ${body.requestId.slice(0, 8)}` : undefined,
      );
    }
    throw err;
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const apiBaseUrl = API_URL;
export { refreshAccessToken };
